/**
 * Copyright © 2025 Farmlands Co-operative Society Limited. All rights reserved.
 *
 * This document contains confidential information and is the property of Farmlands.
 * Unauthorised reproduction or distribution of any part of this publication is prohibited without written consent.
 */

import { FC } from 'react';
import { Activity, Lookup } from '../../api/types/types';
import TestIds from '../../../../testIds';
import { AccountCircleOutlined, CalendarTodayOutlined } from '@mui/icons-material';
import StatusBadge from '../StatusBadge/StatusBadge';
import { capitaliseFirstLetter } from '../../../../util/consts';
import { getActivityTypeLabel } from '../../utils/lookupUtils';
import { getActivityTypeIconById } from '../../utils/getActivityIcon';
import Card from '../../../../components/Card';
import { CardContent, Icon, Stack } from '@mui/material';
import Typography from '../../../../components/Typography';
import dayjs from 'dayjs';

export interface ActivityItemProps {
  activity: Activity;
  lookups?: Lookup[];
  onClick?: (activity: Activity) => void;
}

const ActivityListItem: FC<ActivityItemProps> = ({
  activity,
  lookups = [],
  onClick,
}): JSX.Element => {
  const handleClick = () => {
    if (onClick) {
      onClick(activity);
    }
  };

  const scheduleDate = dayjs(activity.scheduleDate).isValid()
    ? dayjs(activity.scheduleDate).format('DD/MM/YYYY')
    : 'Unknown';

  return (
    <Card
      data-testid={TestIds.feat.activities.activityItem.component}
      onClick={handleClick}
      variant='outlined'
      sx={{ borderRadius: 4, cursor: 'default' }}
    >
      <CardContent
        sx={{
          paddingTop: 3,
        }}
      >
        <Stack direction='row'>
          <Stack flexDirection='column' flex={1} gap={0.5}>
            <Stack
              flexDirection={{ xs: 'column', sm: 'row' }}
              gap={1}
              justifyContent='space-between'
            >
              <Typography
                variant='h4'
                fontWeight={700}
                color='shade100'
                sx={{ wordBreak: 'break-all' }}
              >
                {activity.subject}
              </Typography>

              <StatusBadge size='small' status={activity.status} />
            </Stack>

            {activity.description ? (
              <Typography
                data-testid={TestIds.feat.activities.activityItem.description}
                variant='body2'
                sx={{
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  display: '-webkit-box',
                  WebkitLineClamp: 2,
                  WebkitBoxOrient: 'vertical',
                  pt: { xs: 1, sm: 0, wordBreak: 'break-all' },
                }}
              >
                {activity.description}
              </Typography>
            ) : null}

            <Stack pt={1.5} gap={{ xs: 1, sm: 2 }} direction={{ xs: 'column', sm: 'row' }}>
              <Stack gap={2} direction='row'>
                <Stack direction='row' gap={0.5}>
                  <Icon color='disabled'>{getActivityTypeIconById(activity.activityType)}</Icon>
                  <Typography variant='body2' fontWeight={700}>
                    {capitaliseFirstLetter(getActivityTypeLabel(lookups, activity.activityType))}
                  </Typography>
                </Stack>
                <Stack direction='row' gap={0.5}>
                  <AccountCircleOutlined color='disabled' />
                  <Typography variant='body2'>{activity.createdBy}</Typography>
                </Stack>
              </Stack>
              <Stack direction='row' gap={0.5}>
                <CalendarTodayOutlined color='disabled' sx={{ width: 20, height: 20 }} />
                <Typography variant='body2'>Scheduled date {scheduleDate}</Typography>
              </Stack>
            </Stack>
          </Stack>
        </Stack>
      </CardContent>
    </Card>
  );
};

export default ActivityListItem;
